import {
  Button,
  IconButton,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import { AddCircle, Delete, Edit } from '@material-ui/icons'
import { Box } from '@mui/material'
import { useEffect } from 'react'
import { RootStateOrAny, useSelector } from 'react-redux'
import styled from 'styled-components'
import Loader from '../Loader/Loader'

const AddressBookTableContainer = styled(TableContainer)`
  .MuiTable-root {
    max-height: 220px;
    background: white;
  }

  .MuiTableBody-root {
    position: relative;
  }

  .actions {
    justify-content: flex-end !important;
    position: absolute;
    right: 10px;
  }

  .truncate {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    max-width: calc(100% - 100px);

    &.shorter {
      max-width: calc(100% - 200px);
    }
  }

  .default-choice {
    color: white;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 12px;
    background: var(--color-gradient);
    display: flex;
    align-items: center;
    padding: 2px 6px 0 6px;
    margin-left: 24px;
  }

  .MuiTable-root {
    table-layout: fixed;
  }
`

interface Props {
  type: string
  setShowModal: (val: boolean) => void
  setShowDeleteModal: (val: boolean) => void
  setSelectedAddress: (val: Address | undefined) => void
  addressId: string
  setAddressId: (val: string) => void
  hideCheckBoxes?: boolean
}

const AddressBookTable = ({
  type,
  setShowModal,
  setShowDeleteModal,
  setSelectedAddress,
  addressId,
  setAddressId,
  hideCheckBoxes,
}: Props) => {
  const { loading, addressBook } = useSelector(
    (state: RootStateOrAny) => state.addressList
  )

  const addAddress = () => {
    setSelectedAddress(undefined)
    setShowModal(true)
  }

  const editAddress = (address: Address) => {
    setSelectedAddress(address)
    setShowModal(true)
  }

  const deleteAddress = (address: Address) => {
    setSelectedAddress(address)
    setShowDeleteModal(true)
  }

  const Row = ({ address }: { address: Address }) => {
    const {
      companyName,
      streetAddress,
      aptSuiteOther,
      city,
      state,
      zipCode,
      isDefaultShipping,
      isDefaultReturn,
      _id,
    } = address

    const handleChange = () => {
      setAddressId(_id)
    }

    useEffect(() => {
      if (type === 'Shipping' && !addressId && isDefaultShipping) {
        setAddressId(_id)
      }

      if (type === 'Return' && !addressId && isDefaultReturn) {
        setAddressId(_id)
      }
    }, [_id, isDefaultShipping, isDefaultReturn])

    return (
      <TableRow>
        <TableCell width="100%">
          <div className="flex space-between">
            <Box sx={{ display: 'flex', width: '100%' }}>
              {!hideCheckBoxes && (
                <Radio
                  className="p-b-0 p-t-0 p-l-0 p-r-0 m-r-10"
                  color="primary"
                  checked={addressId === _id}
                  onChange={handleChange}
                />
              )}

              <span
                className={`truncate ${(isDefaultShipping && type === 'Shipping') ||
                    (isDefaultReturn && type === 'Return')
                    ? 'shorter'
                    : ''
                  }`}
              >
                {companyName && companyName}, {streetAddress}
                {aptSuiteOther !== '' && ' '}
                {aptSuiteOther}, {city}, {state} {zipCode}
              </span>
              {type === 'Shipping' && isDefaultShipping && (
                <div className="default-choice">Default</div>
              )}
              {type === 'Return' && isDefaultReturn && (
                <div className="default-choice">Default</div>
              )}
            </Box>
            <div className="actions">
              <IconButton
                className="m-r-10"
                onClick={() => editAddress(address)}
              >
                <Edit />
              </IconButton>
              <IconButton onClick={() => deleteAddress(address)}>
                <Delete />
              </IconButton>
            </div>
          </div>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <div className="flex m-t-30 space-between">
        <h5 className="fs18">{type} address</h5>
        <Button variant="contained" color="primary" onClick={addAddress}>
          Add address
        </Button>
      </div>
      <AddressBookTableContainer className="scrollable m-b-40 m-t-10">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell width="100%">Address book</TableCell>
            </TableRow>
          </TableHead>

          {loading && <Loader />}

          {addressBook && addressBook.length ? (
            <TableBody>
              {type === 'Shipping'
                ? addressBook
                  .sort((a: Address, b: Address) =>
                    a.isDefaultShipping &&
                      b.isDefaultShipping &&
                      a.isDefaultShipping < b.isDefaultShipping
                      ? 1
                      : -1
                  )
                  .map((address: Address, index: number) => (
                    <Row key={index} address={address} />
                  ))
                : addressBook
                  .sort((a: Address, b: Address) =>
                    a.isDefaultReturn &&
                      b.isDefaultReturn &&
                      a.isDefaultReturn < b.isDefaultReturn
                      ? 1
                      : -1
                  )
                  .map((address: Address, index: number) => (
                    <Row key={index} address={address} />
                  ))}
            </TableBody>
          ) : (
            <TableBody style={{ height: 220 }}>
              <div onClick={addAddress} className="empty">
                <AddCircle />
                <div className="gray-text">Add a new address</div>
              </div>
            </TableBody>
          )}
        </Table>
      </AddressBookTableContainer>
    </>
  )
}

export default AddressBookTable
